import React from "react";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import { get, size, includes, map, filter, debounce, flatten } from "lodash";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import Pagination from "@material-ui/lab/Pagination";
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import { fetchPost } from "../../../api/fetch";
import DownloadIcon from "../../Common/DownloadIcon";
import ScrollableTable from "../../Common/Table/ScrollableTable";
import CloseIcon from "../../Common/CloseIcon";
import CompanyCountDialogRow from "./CompanyCountDialogRow";
import { getLov } from "../../../utils/lov";
import { getProfileTypeIcon } from "../../../utils/profile";
import { exportTableToCsv } from "../../../utils/data";
import { formatDate } from "../../../utils/date";
import colors from "../../../assets/style/colors";
import ProfileSearchInput from "../../Common/ProfileSearchInput";
import ProfileDialog from "../../Profiles/ProfileDialog";

const styles = {
  dialogPaper: {
    minWidth: "768px",
    maxWidth: "calc(100vw - 100px)",
    height: "calc(100vh - 30px)",
    maxHeight: "unset",
    padding: "12px 0px 0px",
  },
  closeIcon: {
    paddingRight: "12px",
    display: "flex",
    justifyContent: "flex-end",
  },
  mainContent: {
    padding: "12px",
    maxWidth: "calc(100% - 24px)",
  },
  tableContainer: {
    padding: "0px",
    minWidth: "768px",
    overflow: "auto",
    maxHeight: "calc(100vh - 164px)",
    "@media print": {
      transform: "scale(0.98) translate(-0.5cm)",
    },
  },
  link: {
    color: colors.primary,
    cursor: "pointer",
    paddingLeft: "10px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
};

const columns = [
  {
    name: "profiles_profile",
    minWidth: 200,
    filter: {
      name: "profile_name",
      type: "input",
    },
  },
  {
    name: "site_profileType",
    minWidth: 150,
    width: 150,
    filter: {
      name: "profile_type_ids",
      type: "searchableMultiple",
    },
  },
  {
    name: "dashboard_carrier",
    minWidth: 150,
    width: 150,
    filter: {
      name: "profile_carrier_ids",
      type: "searchableMultiple",
    },
  },
  {
    name: "profiles_tag",
    minWidth: 150,
    width: 150,
    filter: {
      name: "profile_tag_ids",
      type: "searchableMultiple",
    },
  },
  {
    name: "site_currentSite",
    minWidth: 150,
    width: 150,
    filter: {
      name: "zone_ids",
      type: "multilevelSelect",
    },
  },
  {
    name: "site_currentZone",
    minWidth: 150,
    width: 150,
  },
  {
    name: "site_assignedSiteZone",
    minWidth: 150,
    width: 150,
  },
];

const CompanyCountDialog = (props) => {
  const { classes, language, companies, open, setOpen } = props;
  const [data, setData] = React.useState();
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [totalPages, setTotalPages] = React.useState(1);
  const [lov, setLov] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [filters, setFilters] = React.useState({
    ...props.filters,
    profile_tag_ids: [],
    profile_carrier_ids: [],
    zone_ids: [],
  });
  const [profileGlobal, setProfileGlobal] = React.useState();
  const printRef = React.useRef();

  const onClose = () => {
    setData();
    setOpen(false);
    if (props.onClose) props.onClose();
  };

  const handleFilterChange = (value, field) => {
    if (includes(value, "selectAll")) {
      value = map(get(lov, [field]), (item) => item.id);
    } else if (includes(value, "selectNone")) {
      value = [];
    }
    const newFilters = update(filters, {
      [field]: { $set: value },
    });
    setFilters(newFilters);
    setPage(1);
    if (field === "profile_name") {
      if (typeof value !== "string") {
        newFilters.profile_name = null;
      }
      handleProfileNameChange(newFilters);
    }
  };

  const handleProfileNameChange = React.useCallback(
    debounce((newFilters) => {
      fetchData(newFilters);
    }, 1000),
    []
  );

  const handleFilterClose = () => {
    setShouldUpdate(true);
  };

  React.useEffect(() => {
    if (shouldUpdate) {
      setShouldUpdate(false);
      fetchData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdate]);

  React.useEffect(() => {
    if (open) {
      const newFilters = {
        ...props.filters,
        profile_tag_ids: [],
        profile_carrier_ids: [],
        profile_name: null,
      };
      setFilters(newFilters);
      fetchLov(newFilters);
      fetchData(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchLov = async (newFilters) => {
    try {
      const site_ids =
        get(newFilters, ["site_ids"]) || get(filters, ["site_ids"]);
      let zones = map(
        filter(flatten(map(companies, (c) => c.sites)), (s) =>
          includes(site_ids, s.id)
        ),
        (s) => ({
          ...s,
          options: s.zones,
        })
      );

      if (site_ids.includes(-1)) {
        zones = [
          {
            id: -1,
            name: I18n.get("dashboard_Offsite"),
            active: true,
          }
        ]
      }

      const lov = {
        profile_type_ids: filter(
          await getLov("profileTypes"),
          (item) => item.active
        ),
        profile_tag_ids: filter(await getLov("tags"), (item) => item.active),
        profile_carrier_ids: filter(await getLov(89), (item) => item.active),
        zone_ids: zones,
      };
      setLov(lov);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchData = async (filters) => {
    setIsFetching(true);
    try {
      let res = await fetchPost("onsite", {
        pagination: {
          limit: rowsPerPage + 1,
          offset: (page - 1) * rowsPerPage,
        },
        filters: {
          ...filters,
          active: true,
        },
      });

      if (size(get(res, ["profiles"])) > rowsPerPage) {
        res.profiles = res.profiles.slice(0, rowsPerPage);
        setTotalPages(page + 1)
      } else {
        setTotalPages(page)
      }

      setData(res);
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  const dataFunctions = [
    (item) =>
      get(item, ["profile_name"]) ||
      `${get(item, ["profile_firstname"])} ${get(item, ["profile_lastname"])}`,
    (item) => get(item, [`profile_type_name_${language}`]),
    (item) => get(item, [`profile_carrier_name_${language}`]) || "",
    (item) => get(item, [`tag_name_${language}`]),
    (item) => get(item, ["site_name"]),
    (item) => get(item, ["zone_name"]),
    (item) => `${get(item, ["assigned_site_name"]) || " - "} / ${get(item, ["assigned_zone_name"]) || " - "}`,
  ];

  return (
    <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
      <Grid
        container
        direction="column"
        className="print-margin"
        ref={printRef}
      >
        <Box className="only-print">{formatDate(Date.now())}</Box>
        <Grid item>
          <Grid container alignItems="center">
            <Grid item style={{ width: "350px", paddingLeft: "12px" }}>
              <ProfileSearchInput
                profileType={1}
                allTypes
                title={"profileGlobalSearch"}
                selectProfile={(profile) => setProfileGlobal(profile)}
                sizes={{
                  input: 250,
                  inputDropdown: 350,
                  profile: 420,
                }}
                express
                noMinHeight={true}
                placeholder={I18n.get("profiles_searchProfile")}
                disableNewProfile
                noIcon
              />
              {profileGlobal && (
                <ProfileDialog
                  profileId={get(profileGlobal, ["id"])}
                  name={
                    get(profileGlobal, ["name"]) ||
                    `${get(profileGlobal, ["firstname"])} ${get(profileGlobal, [
                      "lastname",
                    ])}`
                  }
                  blockLevel2={true}
                  onClose={() => {
                    setProfileGlobal();
                    fetchData(filters);
                  }}
                  open={profileGlobal}
                />
              )}
            </Grid>
            <Grid
              item
              xs
              className="flexCenter"
              style={{ justifyContent: "flex-start" }}
            >
              <Grid container justify="flex-start" alignItems="center">
                <Grid item style={{ paddingRight: "10px" }}>
                  {I18n.get("dashboard_showing")}
                </Grid>
                <Grid
                  item
                  style={{ fontSize: "1.428em", paddingRight: "10px" }}
                >
                  {`${size(get(data, ["profiles"]))} = `}
                </Grid>
                {map(get(data, ["profile_types"]), (type) => (
                  <Grid
                    item
                    key={get(type, ["profile_type_id"])}
                    className="flexCenter"
                    style={{ paddingRight: "30px" }}
                  >
                    <img
                      src={getProfileTypeIcon(get(type, ["profile_type_id"]))}
                      alt="profile_icon"
                      height="20px"
                      style={{ paddingRight: "10px" }}
                    />
                    <span style={{ fontSize: "1.428em", paddingRight: "10px" }}>
                      {size(
                        filter(
                          get(data, ["profiles"]),
                          (p) =>
                            p.profile_type_id === get(type, ["profile_type_id"])
                        )
                      )}
                    </span>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item className={classes.closeIcon}>
              <CloseIcon closeFunction={onClose} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item container direction="column" className={classes.mainContent}>
          <Grid item>
            <Grid container>
              <Grid item xs className="text-54">
                {I18n.get("dashboard_lastDetectionOfProfiles")}
              </Grid>
              <Grid item style={{ paddingRight: "20px" }}>
                {totalPages > 1 &&
                  <Pagination
                    style={{ top: -5 }}
                    count={totalPages}
                    color="primary"
                    page={page}
                    onChange={(e, p) => {
                      setPage(p);
                      setShouldUpdate(true);
                    }}
                  />
                }
              </Grid>
              <Grid item className="text-54" style={{ paddingRight: "20px" }}>
                {I18n.get("dashboard_profilesPerPage")}
              </Grid>
              <Grid item style={{ paddingRight: "20px" }}>
                <Select
                  id="profiles-per-page"
                  value={rowsPerPage}
                  style={{ top: -5, width: 53, textAlign: "center" }}
                  onChange={(event) => {
                    setRowsPerPage(event.target.value);
                    setPage(1);
                    setShouldUpdate(true);
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={75}>75</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Grid>
              <Grid item>
                <DownloadIcon
                  exportCsv={() =>
                    exportTableToCsv(
                      "Inventory",
                      columns,
                      get(data, ["profiles"]),
                      dataFunctions,
                      I18n.get
                    )
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.tableContainer}>
            <ScrollableTable
              fetchData={fetchData}
              columns={columns}
              hasMore={false}
              isFetching={isFetching}
              stickyHeader
              lov={lov}
              filters={filters}
              handleFilterChange={handleFilterChange}
              handleFilterClose={handleFilterClose}
            >
              {map(get(data, ["profiles"]), (item, index) => (
                <CompanyCountDialogRow
                  key={index}
                  item={item}
                  index={index}
                  language={language}
                  refresh={() => fetchData(filters)}
                />
              ))}
            </ScrollableTable>
          </Grid>
        </Grid>
        <Box className="only-print">{window.location.href}</Box>
      </Grid>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    companies: get(state, ["user", "companies"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(CompanyCountDialog));
